import {
  ApolloClient,
  createHttpLink,
  InMemoryCache,
  from,
  gql,
  fromPromise,
  ApolloLink,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import store from "../../../store";
import {
  _logout_user,
  set_user_token,
} from "../../../store/actions/login_actions";
import { calculator } from "../../../base/utils";
import { _Cart_Clean } from "../../../store/actions/cart_actions";
import i18n from "../../../i18n";

let isRefreshing = false;
let pendingRequests = [];
let token;

const resolvePendingRequests = () => {
  pendingRequests.map((callback) => callback());
  pendingRequests = [];
};

const saleorHttpLink = createHttpLink({
  uri: process.env.REACT_APP_API_URL,
});

const walletHttpLink = createHttpLink({
  uri: `${process.env.REACT_APP_API_WALLET_URL}`,
});

const otpHttpLink = createHttpLink({
  uri: `${process.env.REACT_APP_API_URL_OTP_URL}`,
});

const authHttpLink = createHttpLink({
  uri: `${process.env.REACT_APP_API_AUTH_URL}`,
});

const getRefreshToken = () => {
  const refreshAuthLinkClient = setContext((_, { headers }) => ({
    headers: {
      ...headers,
      "authorization-bearer": null,
    },
  }));

  const newClient = new ApolloClient({
    link: from([refreshAuthLinkClient, saleorHttpLink]),
    cache: new InMemoryCache(),
  });

  const GET_TOKEN = gql`
    mutation tokenRefresh($refreshToken: String) {
      tokenRefresh(refreshToken: $refreshToken) {
        token
      }
    }
  `;

  const refreshToken = store.getState().Login?.refreshToken;

  return newClient
    .mutate({
      mutation: GET_TOKEN,
      variables: { refreshToken },
    })
    .then((res) => {
      const token = res?.data?.tokenRefresh?.token;
      if (token) {
        return token;
      }
      throw new Error("No token returned");
    })
    .catch((err) => {
      console.error("Refresh token error:", err);
      throw err;
    });
};

const appendTokenLink = new ApolloLink((operation, forward) => {
  const token = store.getState()?.Login?.token || null;

  operation.setContext(({ headers }) => ({
    headers: {
      ...headers,
      "authorization-bearer": token,
    },
  }));

  return forward(operation);
});

const fetchTokenLink = (operation, forward) => {
  if (!isRefreshing) {
    isRefreshing = true;

    return fromPromise(
      getRefreshToken()
        .then((accessToken) => {
          if (!accessToken) throw new Error("No access token");

          token = accessToken;
          store.dispatch(set_user_token(accessToken));

          operation.setContext(({ headers = {} }) => ({
            headers: {
              ...headers,
              "authorization-bearer": accessToken,
            },
          }));

          resolvePendingRequests();
          isRefreshing = false;

          return accessToken;
        })
        .catch(() => {
          resolvePendingRequests();
          store.dispatch(_Cart_Clean());
          store.dispatch(_logout_user());
          calculator();
          try {
            localStorage.removeItem("cid");
          } catch (_) {
            /* ignore */
          }

          const lang = i18n.language;
          if (window.location.pathname !== `/${lang}/`) {
            window.location.replace(`/${lang}/`);
          }

          isRefreshing = false;
          return null;
        })
    )
      .filter(Boolean)
      .flatMap(() => forward(operation));
  }
  return fromPromise(
    new Promise((resolve) => {
      pendingRequests.push(() => resolve());
    })
  ).flatMap(() => {
    operation.setContext(({ headers }) => ({
      headers: {
        ...headers,
        "authorization-bearer": token,
      },
    }));
    return forward(operation);
  });
};

const graphqlErrorsLink = onError(
  ({ graphQLErrors, operation, forward, networkError }) => {
    if (graphQLErrors?.length) {
      const errorCode = graphQLErrors[0]?.extensions?.exception?.code;

      if (
        errorCode === "ExpiredSignatureError" ||
        errorCode === "InvalidSignatureError"
      ) {
        return fetchTokenLink(operation, forward);
      }
    }

    // if (networkError) {
    //   let lang =
    //     SUPPORTED_LANGS.find((lng) => window.location.pathname.includes(lng)) ||
    //     DEFAULT_LANG;
    //   if (
    //     networkError.statusCode >= 500 &&
    //     window.location.pathname !== `/${lang}/high-traffic`
    //   ) {
    //     window.location.href = `/${lang}/high-traffic`;
    //   }
    // }
  }
);

// eslint-disable-next-line
const client = new ApolloClient({
  link: from([
    appendTokenLink,
    graphqlErrorsLink,
    ApolloLink.split(
      (operation) => operation.getContext().clientName === "auth",
      authHttpLink,
      ApolloLink.split(
        (operation) => operation.getContext().clientName === "wallet",
        walletHttpLink,
        ApolloLink.split(
          (operation) => operation.getContext().clientName === "otp",
          otpHttpLink,
          saleorHttpLink
        )
      )
    ),
  ]),
  cache: new InMemoryCache(),
});

export { client };
